import * as React from "react"

import SimpleModal from "@/shadcn/components/SimpleModal"
import { ChangeSummary, type ChangeRow, type ChangeTotal } from "./ChangeSummary"
import { billDate, type ChangePreview, money, number } from "./format"
import { limitLabel, listJoin } from "./plans"

/**
 * Confirms a move between plans: up, down, or back onto a canceled one. Figma:
 * S311lGXE6JSyTnNAXHmtR7, page 30255:7135. Section "Plan upgrade · move to a bigger
 * plan" (30262:4631): to Plus 30262:4632, to Pro 30262:5307, trial to Plus 30262:5619.
 * "Add-ons / Restart canceled plan" 30261:6149. Section "Downgrade · move to a
 * smaller plan" (30263:6471): smaller plan 30263:6905, during a trial 30263:7216.
 *
 * Downgrades take effect at once, with the unused part credited, so the downgrade
 * copy says so rather than the frames' "You'll keep Pro until Nov 3".
 */

export type PlanSide = {
  /** The plan's name without "dlvr.it", e.g. "Pro". */
  label: string
  outputs: number
  inputs: number
  /** Posts a month; 0 is no limit. */
  postLimit: number
  /** Recurring price per billing term. */
  price: number
}

type PlanChangeDialogProps = {
  kind: "upgrade" | "trial" | "restart" | "downgrade" | "downgradeTrial"
  from: PlanSide
  to: PlanSide
  annual: boolean
  /** What an upgrade to Plus or Agency+ adds, in sentence form. */
  features?: string[] | null
  /** When the trial ends, for a trial switch. */
  trialEnds?: string | number | null
  /** The plan's next bill date, used until the preview gives one. */
  nextBilling: string
  preview: ChangePreview
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
  onClose: () => void
}

export default function PlanChangeDialog({
  kind,
  from,
  to,
  annual,
  features,
  trialEnds,
  nextBilling,
  preview,
  busy,
  onConfirm,
  onCancel,
  onClose,
}: PlanChangeDialogProps) {
  const ready = preview.status === "ready" ? preview : null
  const period = annual ? "year" : "month"
  const date = billDate(ready?.nextBillDate ?? nextBilling)
  const priceLabel = annual ? "Annual price" : "Monthly price"
  const limit = (n: number) => limitLabel(n, number)
  const posts = (n: number) => limitLabel(n, number, 0)

  let title: string, description: string, cancelLabel: string, confirmLabel: string
  let rows: ChangeRow[]
  let total: ChangeTotal | null

  if (kind === "downgrade" || kind === "downgradeTrial") {
    const trial = kind === "downgradeTrial"
    const ends = trialEnds ? billDate(trialEnds) : date
    title = trial ? `Switch your trial to ${to.label}?` : `Switch to ${to.label}?`
    description = trial
      ? `Your trial continues on ${to.label} until ${ends}. We won't charge you until it ends.`
      : `${to.label} starts now. We'll credit the unused part of your ${from.label} plan to your account.`
    rows = [
      { label: "Social outputs", from: limit(from.outputs), to: limit(to.outputs) },
      { label: "Feeds & sites", from: limit(from.inputs), to: limit(to.inputs) },
      ...(from.postLimit !== to.postLimit
        ? [{ label: "Posts per month", from: posts(from.postLimit), to: posts(to.postLimit) }]
        : []),
      { label: trial ? "Price after trial" : priceLabel, from: money(from.price), to: money(to.price) },
    ]
    if (trial || preview.status === "error") {
      total = null
    } else if (ready && ready.credit <= 0) {
      total = { label: "Due today", amount: money(ready.dueToday), note: `Your next bill is ${money(to.price)} on ${date}.` }
    } else {
      total = {
        label: "Credit to your account",
        amount: ready ? money(ready.credit) : undefined,
        note: `Your next bill is ${money(to.price)} on ${date}.`,
      }
    }
    cancelLabel = `Keep ${from.label}`
    confirmLabel = `Switch to ${to.label}`
  } else if (kind === "restart") {
    const shortDate = billDate(nextBilling).replace(/, \d{4}$/, "")
    title = `Restart your ${to.label} plan?`
    description = `Your plan won't end on ${billDate(nextBilling)}. We'll keep billing you each ${period}.`
    rows = [
      { label: "Plan", from: `Ends ${shortDate}`, to: `Renews ${shortDate}` },
      { label: "Social outputs", to: limit(to.outputs) },
      { label: priceLabel, to: money(to.price) },
    ]
    total = { label: "Due today", amount: money(0), note: `Your next bill is ${money(to.price)} on ${billDate(nextBilling)}.` }
    cancelLabel = "Cancel"
    confirmLabel = `Restart ${to.label} plan`
  } else if (kind === "trial") {
    const ends = trialEnds ? billDate(trialEnds) : date
    title = `Switch your trial to ${to.label}?`
    description = `You'll have every ${to.label} feature for the rest of your trial. We won't charge you until it ends.`
    rows = [
      { label: "Plan", from: `${from.label} trial`, to: `${to.label} trial` },
      { label: "Trial ends", to: ends },
    ]
    total = {
      label: "Due today",
      amount: ready ? money(ready.dueToday) : preview.status === "error" ? money(0) : undefined,
      note: `Then ${money(to.price)} a ${period}, starting ${ends}.`,
    }
    cancelLabel = `Stay on ${from.label} trial`
    confirmLabel = `Switch to ${to.label}`
  } else {
    title = `Upgrade to ${to.label}?`
    description = features?.length
      ? `You keep everything in ${from.label} and add ${listJoin(features)}.`
      : `You'll post to more destinations from more sources${
          from.postLimit !== 0 && to.postLimit === 0 ? ", with no monthly post limit" : ""
        }.`
    rows = [
      { label: "Social outputs", from: limit(from.outputs), to: limit(to.outputs) },
      { label: "Feeds & sites", from: limit(from.inputs), to: limit(to.inputs) },
      ...(from.postLimit !== to.postLimit
        ? [{ label: "Posts per month", from: posts(from.postLimit), to: posts(to.postLimit) }]
        : []),
      { label: priceLabel, from: money(from.price), to: money(to.price) },
    ]
    total =
      preview.status === "error"
        ? null
        : {
            label: "Due today",
            amount: ready ? money(ready.dueToday) : undefined,
            note: `Prorated for the rest of this ${period}. Your next bill is ${money(to.price)} on ${date}.`,
          }
    cancelLabel = `Stay on ${from.label}`
    confirmLabel = ready && ready.dueToday > 0 ? `Upgrade and pay ${money(ready.dueToday)}` : `Upgrade to ${to.label}`
  }

  return (
    <SimpleModal
      title={title}
      description={description}
      cancelLabel={cancelLabel}
      confirmLabel={confirmLabel}
      confirmDisabled={kind !== "restart" && kind !== "downgradeTrial" && preview.status === "loading"}
      busy={busy}
      busyLabel="Saving..."
      onConfirm={onConfirm}
      onCancel={onCancel}
      onClose={onClose}
    >
      <ChangeSummary rows={rows} total={total} />
      {kind === "upgrade" && preview.status === "error" ? (
        <p className="tw:text-xs tw:text-muted-foreground">
          You'll be charged a prorated amount today. Your next bill is {money(to.price)} on {date}.
        </p>
      ) : null}
    </SimpleModal>
  )
}
